import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabaseClient';

// Types
export interface Conversation {
  id: string;
  user_id: string | null;
  guest_email: string | null;
  guest_name: string | null;
  status: 'open' | 'closed';
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_type: 'customer' | 'staff';
  sender_id: string | null;
  content: string;
  created_at: string;
}

export interface ConversationSummary extends Conversation {
  customer_name: string | null;
  user_email: string | null;
  last_message: string | null;
  last_message_at: string | null;
  last_message_sender_type: 'customer' | 'staff' | null;
  message_count: number;
}

export interface ConversationWithMessages extends Conversation {
  messages: Message[];
  customer_name?: string;
}

// Guest session management
const GUEST_SESSION_KEY = 'ask_lotus_guest_session';

interface GuestSession {
  conversationId: string;
  email: string;
  name?: string;
  createdAt: string;
}

export const getGuestSession = (): GuestSession | null => {
  try {
    // Use sessionStorage for privacy - cleared when browser/tab closes
    const session = sessionStorage.getItem(GUEST_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  } catch {
    return null;
  }
};

export const setGuestSession = (session: GuestSession): void => {
  // Use sessionStorage for privacy - cleared when browser/tab closes
  sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
};

export const clearGuestSession = (): void => {
  sessionStorage.removeItem(GUEST_SESSION_KEY);
};

// Conversation operations
export const createConversation = async (
  userId: string | null,
  guestEmail?: string,
  guestName?: string
): Promise<Conversation | null> => {
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      user_id: userId,
      guest_email: guestEmail || null,
      guest_name: guestName || null,
      status: 'open'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating conversation:', error);
    return null;
  }

  return data;
};

export const getConversationByUserId = async (userId: string): Promise<Conversation | null> => {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'open')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error getting conversation:', error);
  }

  return data || null;
};

export const getConversationById = async (conversationId: string): Promise<Conversation | null> => {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .single();

  if (error) {
    console.error('Error getting conversation by ID:', error);
    return null;
  }

  return data;
};

export const closeConversation = async (conversationId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('conversations')
    .update({ status: 'closed' })
    .eq('id', conversationId);

  if (error) {
    console.error('Error closing conversation:', error);
    return false;
  }

  return true;
};

export const reopenConversation = async (conversationId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('conversations')
    .update({ status: 'open' })
    .eq('id', conversationId);

  if (error) {
    console.error('Error reopening conversation:', error);
    return false;
  }

  return true;
};

// Push notification helper
const notifyStaffOfNewMessage = async (
  conversationId: string,
  messagePreview: string
): Promise<void> => {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-push-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        notifyAllStaff: true,
        title: 'New Customer Message',
        body: messagePreview.length > 100 ? messagePreview.substring(0, 97) + '...' : messagePreview,
        data: { conversationId },
        notificationType: 'message',
      }),
    });
    console.log('[MessageService] Push notification sent to staff');
  } catch (error) {
    // Non-blocking - don't fail message send if notification fails
    console.log('[MessageService] Push notification failed (non-blocking):', error);
  }
};

// Message operations
export const getMessages = async (conversationId: string, limit: number = 10): Promise<Message[]> => {
  // Get most recent messages (descending), then reverse for chronological display
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error getting messages:', error);
    return [];
  }

  // Reverse to get chronological order (oldest first)
  return (data || []).reverse();
};

export const sendMessage = async (
  conversationId: string,
  content: string,
  senderType: 'customer' | 'staff',
  senderId?: string | null
): Promise<Message | null> => {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      content,
      sender_type: senderType,
      sender_id: senderId || null
    })
    .select()
    .single();

  if (error) {
    console.error('Error sending message:', error);
    return null;
  }

  // Trigger push notification to staff when customer sends message
  if (senderType === 'customer' && data) {
    notifyStaffOfNewMessage(conversationId, content.trim());
  }

  return data;
};

// Admin/Crew operations
export const getAllConversations = async (
  statusFilter?: 'open' | 'closed' | 'all'
): Promise<ConversationSummary[]> => {
  let query = supabase
    .from('conversation_summaries')
    .select('*')
    .order('updated_at', { ascending: false });

  if (statusFilter && statusFilter !== 'all') {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error getting all conversations:', error);
    return [];
  }

  return data || [];
};

export const getConversationWithMessages = async (
  conversationId: string
): Promise<ConversationWithMessages | null> => {
  // Get conversation
  const { data: conversation, error: convError } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .single();

  if (convError) {
    console.error('Error getting conversation:', convError);
    return null;
  }

  // Get messages
  const messages = await getMessages(conversationId);

  // Get customer name
  let customerName = conversation.guest_name || conversation.guest_email;
  if (conversation.user_id) {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name, preferred_name, email')
      .eq('id', conversation.user_id)
      .single();

    if (profile) {
      customerName = profile.preferred_name || profile.full_name || profile.email;
    }
  }

  return {
    ...conversation,
    messages,
    customer_name: customerName
  };
};

// Get unread message count for staff (conversations with customer messages they haven't seen)
export const getUnreadConversationCount = async (): Promise<number> => {
  // For now, count open conversations as "unread"
  // In future, could add a "last_read_by_staff" timestamp
  const { data, error } = await supabase
    .from('conversations')
    .select('id', { count: 'exact' })
    .eq('status', 'open');

  if (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }

  return data?.length || 0;
};

// Rate limiting helper
const MESSAGE_RATE_LIMIT = 5; // messages per minute
const MESSAGE_RATE_WINDOW = 60000; // 1 minute in ms
const messageTimestamps: number[] = [];

export const canSendMessage = (): boolean => {
  const now = Date.now();
  // Remove timestamps older than the window
  while (messageTimestamps.length > 0 && messageTimestamps[0] < now - MESSAGE_RATE_WINDOW) {
    messageTimestamps.shift();
  }
  return messageTimestamps.length < MESSAGE_RATE_LIMIT;
};

export const recordMessageSent = (): void => {
  messageTimestamps.push(Date.now());
};
